import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import {
	getEntityById,
	getStatusLabelFromPipelineFilters,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { Button, Popconfirm, message } from 'antd';
import {
	cycleEventsRestCrudThunksForBuyer,
	cycleEventsRestCrudThunksForSupplier,
} from '../../thunks/cycle_events_thunks';
import GeneralStatusDisplay from '../AdminCycleEventsIndexPage/GeneralStatusDisplay';
import CycleEventPipeLineFilters from '../AdminCycleEventsIndexPage/CycleEventPipelineFilters';
import { ACC_RUN_STATUSES } from '../../utils/AssetCycleUtils';
import DateTimeHover from '../date_time_component/DateTime';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';

interface AssetCycleCountEventDetailPageProps {
	match: any;
	location: any;
	history: any;
	cycleEvent: any;
	cycleRunsFetching: boolean;
	companyConfig: any;
	getCycleEvent(id: number | string): Promise<any>;
	childRoutes: any[];
	deleteCycleEvent(cycleEvent: any): Promise<any>;
	archiveCycleEvent(cycleEvent: any): Promise<any>;
	unarchiveCycleEvent(cycleEvent: any): Promise<any>;
	userType: string;
}

const EDITABLE_STATUSES = ['notStarted', 'inProgress'];

const AssetCycleCountEventDetailPage: FC<AssetCycleCountEventDetailPageProps> = ({
	match,
	location,
	history,
	companyConfig,
	getCycleEvent,
	childRoutes,
	cycleEvent,
	deleteCycleEvent,
	archiveCycleEvent,
	unarchiveCycleEvent,
	userType,
}): React.ReactElement => {
	const cycleRunId = useMemo(() => match.params.id, [match.params.id]);

	// Target the overview route directly (not the parent `/workflows/cycleevents`, whose
	// redirect drops the query string and would lose the status filter).
	const defaultPath = `/${userType}/workflows/cycleevents/overview`;

	// Return to the originating overview (preserving its Active/Archived/All status filter and
	// query) rather than always the default view. `onRow` stashes the overview's path in
	// `backTab` and the rest of its query string in `backlinkParams`.
	const backLinkTo = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);
		const overviewBasePath = searchParams.get('backTab') || defaultPath;
		const backlinkParams = searchParams.get('backlinkParams');
		return backlinkParams ? `${overviewBasePath}?${backlinkParams}` : overviewBasePath;
	}, [location.search, defaultPath]);

	useEffect(() => {
		getCycleEvent(cycleRunId);
	}, [cycleRunId]);

	const runCounts = nullSafeGetOrElse('assetCycleCountRunCountsBasedOnStatus', cycleEvent, []);
	const completedEntry = runCounts.find(function (c) {
		return c.categoryName === ACC_RUN_STATUSES.CHANGES_APPLIED;
	});
	const completedCount = nullSafeGetOrElse('count', completedEntry, 0);

	const isEditEnabled = useMemo(
		() => EDITABLE_STATUSES.includes(nullSafeGetOrElse('status', cycleEvent, '')),
		[cycleEvent]
	);

	const isArchived = nullSafeGetOrElse('isArchived', cycleEvent, false);

	const handleArchive = useCallback(() => {
		archiveCycleEvent(cycleEvent).then(() => {
			message.success('Asset cycle event archived.');
			history.push(backLinkTo);
		});
	}, [archiveCycleEvent, cycleEvent, history, backLinkTo]);

	const handleUnarchive = useCallback(() => {
		unarchiveCycleEvent(cycleEvent).then(() => {
			message.success('Asset cycle event restored.');
			getCycleEvent(cycleRunId);
		});
	}, [unarchiveCycleEvent, cycleEvent, getCycleEvent, cycleRunId]);

	return (
		<div>
			<DetailPageHeader
				exactPath={`/${userType}/assetcycleevents/detail/:id`}
				redirectPath={`/${userType}/assetcycleevents/detail/${cycleRunId}/details`}
				backLinkText={'Back to Asset Cycle Runs'}
				backLinkTo={backLinkTo}
				title={nullSafeGet('name', cycleEvent) || ''}
				subtitle={
					<div className="flex flex-row items-center gap-2">
						<GeneralStatusDisplay
							statusKey={nullSafeGet('status', cycleEvent)}
							statusLabel={getStatusLabelFromPipelineFilters(
								CycleEventPipeLineFilters,
								nullSafeGet('status', cycleEvent)
							)}
							className="footnoteDescription"
						/>
						<div className="footnoteregular text-white">
							{completedCount}/{nullSafeGetOrElse('locationCount', cycleEvent, 0)} Runs complete
						</div>
						<div className="footnoteregular text-white">
							Due:{' '}
							<DateTimeHover
								showDate
								timestamp={nullSafeGet('dueAt', cycleEvent)}
								showTime={false}
							/>
						</div>
					</div>
				}
				actions={[
					<Button
						key="edit"
						ghost
						disabled={!isEditEnabled}
						onClick={() => history.push(`/${userType}/assetcycleevents/edit/${cycleRunId}`)}
					>
						Edit
					</Button>,
					isArchived ? (
						<Button key="unarchive" ghost className="ml-4" onClick={handleUnarchive}>
							Unarchive
						</Button>
					) : (
						<Button key="archive" ghost className="ml-4" onClick={handleArchive}>
							Archive
						</Button>
					),
					<Popconfirm
						title="Are you sure you want to delete this asset cycle event?"
						onConfirm={() => deleteCycleEvent(cycleEvent).then(() => history.push(backLinkTo))}
					>
						<Button key="delete" ghost className="ml-4">
							Delete
						</Button>
					</Popconfirm>,
				]}
				tabs={[]}
				backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
				childRoutes={childRoutes}
			/>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	cycleEvent: getEntityById(ownProps.match.params.id, state.cycle_events),
	companyConfig: state.company_config,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getCycleEvent: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier.readOne(id)
				: cycleEventsRestCrudThunksForBuyer.readOne(id)
		),
	deleteCycleEvent: (cycleEvent) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier.delete(cycleEvent)
				: cycleEventsRestCrudThunksForBuyer.delete(cycleEvent)
		),
	archiveCycleEvent: (cycleEvent) =>
		dispatch(
			(ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier
				: cycleEventsRestCrudThunksForBuyer
			).update({ ...cycleEvent, isArchived: true })
		),
	unarchiveCycleEvent: (cycleEvent) =>
		dispatch(
			(ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier
				: cycleEventsRestCrudThunksForBuyer
			).update({ ...cycleEvent, isArchived: false })
		),
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(AssetCycleCountEventDetailPage as any))
);
